import React, { useState } from "react";
import { round2 } from "../../utils/polygonUtils";
import { editorCursor } from "../../utils/editorCursor";

// Renders the walkable-area polygon in TWO layers so it never occludes beacons:
//   layer="fill"    -> the outward dim mask + boundary outline. Placed BELOW the
//                      station/symbol markers (but above the floor image) so the
//                      forbidden zone is dimmed while markers stay bright on top.
//   layer="handles" -> the in-progress dashed line (while drawing) + draggable
//                      vertex handles. Placed ON TOP so vertices stay grabbable
//                      even where they overlap a station.
// Both layers share #floor-plan-canvas's origin, so their pixel coords line up.
//
// Colour scheme ("spotlight cutout"): a neutral WHITE edge with a dark halo, kept
// deliberately distinct from the data markers (beacons #1890ff, zones #fa8c16,
// stations #52c41a, symbols) so the boundary reads as structure, not a marker.
const MASK_FILL = "rgba(0, 0, 0, 0.28)";
const OUTLINE = "#ffffff";
const HANDLE_FILL = "#ffffff";
const HANDLE_RING = "#1f1f1f";
const CLOSE_RING = "#52c41a"; // first vertex when the ring can be closed

// A zero-offset drop-shadow reads as a symmetric halo, giving the white line
// contrast against both the bright floor interior and the dim exterior.
const Halo = ({ id }) => (
  <defs>
    <filter id={id} x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow
        dx="0"
        dy="0"
        stdDeviation="1.2"
        floodColor="#000000"
        floodOpacity="0.55"
      />
    </filter>
  </defs>
);

const AreaPolygonEditor = ({
  layer, // "fill" | "handles"
  vertices, // [[x, y]] meters
  drawing,
  editable,
  viewport, // plane viewport from utils/viewport.js (y-up metres <-> canvas pixels)
  canvasSize,
  dimensions,
  onMoveVertex,
  onRemoveVertex,
  onVertexClick,
}) => {
  // The vertex being held, for its grabbing cursor.
  const [heldIndex, setHeldIndex] = useState(null);
  if (!viewport?.isValid || !vertices) return null;

  const pts = viewport.projectPoints(vertices);
  const pointsAttr = pts.map(([x, y]) => `${x},${y}`).join(" ");

  const svgStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    pointerEvents: "none",
    overflow: "visible",
  };

  // Bottom layer: outward "spotlight" mask (canvas rect MINUS the polygon via
  // even-odd) + the boundary outline. Only meaningful for a closed ring.
  if (layer === "fill") {
    if (drawing || pts.length < 3) return null;
    const outer = `M0,0 H${canvasSize.width} V${canvasSize.height} H0 Z`;
    const inner = `M${pts.map(([x, y]) => `${x},${y}`).join(" L")} Z`;
    return (
      <svg
        data-layer="area"
        width={canvasSize.width}
        height={canvasSize.height}
        style={svgStyle}
      >
        <Halo id="area-outline-halo" />
        <path d={`${outer} ${inner}`} fillRule="evenodd" fill={MASK_FILL} stroke="none" />
        <polygon
          points={pointsAttr}
          fill="none"
          stroke={OUTLINE}
          strokeWidth={2}
          filter="url(#area-outline-halo)"
        />
      </svg>
    );
  }

  // Top layer: in-progress dashed outline (while drawing) + vertex handles.
  const closeable = drawing && pts.length >= 3;

  const startDrag = (index) => (e) => {
    if (!editable) return;
    e.stopPropagation();
    e.preventDefault();
    const svg = e.currentTarget.ownerSVGElement;
    let moved = false;

    const onMove = (ev) => {
      moved = true;
      const rect = svg.getBoundingClientRect();
      const m = viewport.unproject({
        x: ev.clientX - rect.left,
        y: ev.clientY - rect.top,
      });
      const mx = Math.min(Math.max(m.x, 0), dimensions.width);
      const my = Math.min(Math.max(m.y, 0), dimensions.height);
      onMoveVertex(index, [round2(mx), round2(my)]);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setHeldIndex(null);
      if (!moved && onVertexClick) onVertexClick(index);
    };
    setHeldIndex(index);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <svg
        data-layer="area"
        width={canvasSize.width}
        height={canvasSize.height}
        style={svgStyle}
      >
      <Halo id="area-line-halo" />
      {drawing && pts.length >= 2 && (
        <polyline
          points={pointsAttr}
          fill="none"
          stroke={OUTLINE}
          strokeWidth={2}
          strokeDasharray="6 4"
          filter="url(#area-line-halo)"
        />
      )}

      {editable &&
        pts.map(([x, y], i) => {
          const isClosePoint = i === 0 && closeable;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={isClosePoint ? 6 : 5}
              fill={HANDLE_FILL}
              stroke={isClosePoint ? CLOSE_RING : HANDLE_RING}
              strokeWidth={isClosePoint ? 2 : 1.5}
              style={{
                pointerEvents: "auto",
                cursor: editorCursor(drawing ? "place" : "edit", "vertex", {
                  dragging: heldIndex === i,
                }),
              }}
              onMouseDown={startDrag(i)}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (!drawing) onRemoveVertex(i);
              }}
            >
              <title>
                {isClosePoint ? "點擊此點以封閉區域" : "拖曳移動，雙擊刪除"}
              </title>
            </circle>
          );
        })}
    </svg>
  );
};

export default AreaPolygonEditor;
